
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Search, PlusCircle } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import ProfileIcon from './ProfileIcon';
import { SocialProfile } from './types';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface ProfilesListProps {
  profiles: SocialProfile[];
  isSingleSelectionMode: boolean;
  selectedProfiles: string[];
  singleSelectedProfile: string;
  toggleProfileSelection: (id: string) => void;
  setSingleSelectedProfile: (id: string) => void;
  isLoading: boolean;
  handlePageChange?: (page: string) => void; // Add this prop
}

const ProfilesList = ({
  profiles,
  isSingleSelectionMode,
  selectedProfiles,
  singleSelectedProfile,
  toggleProfileSelection,
  setSingleSelectedProfile,
  isLoading,
  handlePageChange,
}: ProfilesListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtrer les profils en fonction du terme de recherche
  const filteredProfiles = profiles.filter(profile => 
    profile.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const navigateToAddProfile = () => {
    if (handlePageChange) {
      handlePageChange('add-profile');
    }
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="p-4 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-md w-full"
          />
        </div>
      </div>

      <div className="px-4 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="py-4 text-center text-gray-500 dark:text-gray-400 text-sm">Chargement...</div>
        ) : filteredProfiles.length > 0 ? (
          isSingleSelectionMode ? (
            <RadioGroup 
              value={singleSelectedProfile} 
              onValueChange={setSingleSelectedProfile}
              className="space-y-2 w-full"
            >
              {filteredProfiles.map((profile) => (
                <div key={profile.id} className="flex items-center py-2 w-full">
                  <RadioGroupItem 
                    value={profile.id} 
                    id={`radio-${profile.id}`} 
                    className="mr-3 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600"
                  />
                  <Label 
                    htmlFor={`radio-${profile.id}`}
                    className="flex items-center flex-1 cursor-pointer"
                  >
                    <div className="flex items-center">
                      <ProfileIcon platform={profile.platform} />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{profile.name}</span>
                    </div>
                    {profile.stats && (
                      <span className={cn(
                        "ml-auto px-1.5 py-0.5 text-white rounded text-xs font-medium",
                        profile.platform === "instagram" ? "bg-orange-500" : 
                        profile.platform === "youtube" ? "bg-red-500" : "bg-blue-500"
                      )}>
                        {profile.stats}
                      </span>
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          ) : (
            <ul className="space-y-2 w-full">
              {filteredProfiles.map((profile) => (
                <li key={profile.id} className="flex items-center py-2 w-full">
                  <Checkbox
                    id={`checkbox-${profile.id}`}
                    checked={selectedProfiles.includes(profile.id)}
                    onCheckedChange={() => toggleProfileSelection(profile.id)}
                    className="mr-3 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600"
                  />
                  <Label 
                    htmlFor={`checkbox-${profile.id}`}
                    className="flex items-center flex-1 cursor-pointer"
                  >
                    <div className="flex items-center">
                      <ProfileIcon platform={profile.platform} />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{profile.name}</span>
                    </div>
                    {profile.stats && (
                      <span className={cn(
                        "ml-auto px-1.5 py-0.5 text-white rounded text-xs font-medium",
                        profile.platform === "instagram" ? "bg-orange-500" : 
                        profile.platform === "youtube" ? "bg-red-500" : "bg-blue-500"
                      )}>
                        {profile.stats}
                      </span>
                    )}
                  </Label>
                </li>
              ))}
            </ul>
          )
        ) : (
          <div className="py-4 text-center">
            <div className="text-gray-500 dark:text-gray-400 text-sm mb-3">
              {searchTerm ? "Aucun profil ne correspond à votre recherche" : "Aucun profil social connecté"}
            </div>
            <div className="flex justify-center">
              <Button 
                onClick={navigateToAddProfile}
                variant="ghost" 
                className="text-blue-600 dark:text-blue-400 text-sm flex items-center hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                <PlusCircle className="h-4 w-4 mr-1" />
                Ajoutez un profil pour commencer
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilesList;
